import React, { useState, useEffect } from "react";
import axios from "axios";

const TestimonialAdminPanel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    quote: "",
    authorName: "",
    authorTitle: "",
    authorImage: "",
    isActive: true,
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get("/api/testimonials");
      setTestimonials(response.data.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch testimonials");
      setLoading(false);
      console.error("Error fetching testimonials:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        // Update existing testimonial
        await axios.put(`/api/testimonials/${editingTestimonial._id}`, formData);
      } else {
        // Create new testimonial
        await axios.post("/api/testimonials", formData);
      }
      
      // Reset form and refresh testimonials
      setFormData({
        quote: "",
        authorName: "",
        authorTitle: "",
        authorImage: "",
        isActive: true,
      });
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (err) {
      setError(`Failed to ${editingTestimonial ? "update" : "create"} testimonial`);
      console.error(`Error ${editingTestimonial ? "updating" : "creating"} testimonial:`, err);
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      quote: testimonial.quote,
      authorName: testimonial.authorName,
      authorTitle: testimonial.authorTitle,
      authorImage: testimonial.authorImage,
      isActive: testimonial.isActive,
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await axios.delete(`/api/testimonials/${id}`);
        fetchTestimonials();
      } catch (err) {
        setError("Failed to delete testimonial");
        console.error("Error deleting testimonial:", err);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await axios.patch(`/api/testimonials/${id}/toggle`);
      fetchTestimonials();
    } catch (err) {
      setError("Failed to toggle testimonial status");
      console.error("Error toggling testimonial status:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      quote: "",
      authorName: "",
      authorTitle: "",
      authorImage: "",
      isActive: true,
    });
    setEditingTestimonial(null);
  };

  if (loading) {
    return <div className="text-center py-5">Loading testimonials...</div>;
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            {/* <div className="card-header pb-0">
              <h6>Testimonials Management</h6>
            </div> */}
            <div className="card-body px-0 pt-0 pb-2">
              {error && <div className="alert alert-danger">{error}</div>}
              
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>{editingTestimonial ? "Edit Testimonial" : "Add New Testimonial"}</h5>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label htmlFor="quote" className="form-label">Quote</label>
                          <textarea
                            className="form-control"
                            id="quote"
                            name="quote"
                            rows="3"
                            value={formData.quote}
                            onChange={handleInputChange}
                            required
                          ></textarea>
                        </div>
                        
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label htmlFor="authorName" className="form-label">Author Name</label>
                            <input
                              type="text"
                              className="form-control"
                              id="authorName"
                              name="authorName"
                              value={formData.authorName}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <label htmlFor="authorTitle" className="form-label">Author Title</label>
                            <input
                              type="text"
                              className="form-control"
                              id="authorTitle"
                              name="authorTitle"
                              value={formData.authorTitle}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <label htmlFor="authorImage" className="form-label">Author Image URL</label>
                          <input
                            type="text"
                            className="form-control"
                            id="authorImage"
                            name="authorImage"
                            value={formData.authorImage}
                            onChange={handleInputChange}
                          />
                        </div>
                        
                        <div className="form-check mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label" htmlFor="isActive">
                            Active
                          </label>
                        </div>
                        
                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary">
                            {editingTestimonial ? "Update Testimonial" : "Add Testimonial"}
                          </button>
                          {editingTestimonial && (
                            <button type="button" className="btn btn-secondary" onClick={resetForm}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="table-responsive p-0">
                <table className="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Author</th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Quote</th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testimonials.map((testimonial) => (
                      <tr key={testimonial._id}>
                        <td>
                          <div className="d-flex px-2 py-1">
                            <div>
                              <img
                                src={testimonial.authorImage || "https://via.placeholder.com/40"}
                                className="avatar avatar-sm me-3 "
                                  style={{ width: "50px", height: "50px", borderRadius: "50%" }}
                                alt={testimonial.authorName}
                              />
                            </div>
                            <div className="d-flex flex-column justify-content-center">
                              <h6 className="mb-0 text-sm">{testimonial.authorName}</h6>
                              <p className="text-xs text-secondary mb-0">{testimonial.authorTitle}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <p className="text-sm font-weight-normal mb-0">
                            {testimonial.quote.length > 50 
                              ? `${testimonial.quote.substring(0, 50)}...` 
                              : testimonial.quote}
                          </p>
                        </td>
                        <td>
                          <span className={`badge badge-sm ${testimonial.isActive ? 'bg-gradient-success' : 'bg-gradient-secondary'}`}>
                            {testimonial.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(testimonial)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(testimonial._id)}
                            >
                              Delete
                            </button>
                            {/* <button
                              className={`btn btn-sm btn-outline-${testimonial.isActive ? 'warning' : 'success'}`}
                              onClick={() => handleToggleStatus(testimonial._id)}
                            >
                              {testimonial.isActive ? 'Disable' : 'Enable'}
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {testimonials.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-secondary">No testimonials found. Add a new testimonial to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialAdminPanel;